// ============================================================
// TMR Portal Admin — formulário do molde global ↔ payload
// ============================================================
import { parseDecimal } from '../rates/form';
import type { CreateTmrRateRequest, TmrRate, UpdateTmrRateRequest } from './contract';

/**
 * Whether a change reaches the Empresas that already hold this line.
 *
 * `unset` is a real state, not a missing value: the form starts there and refuses to submit until the
 * operator picks one, because both answers are consequential and a default would be the screen answering
 * for them (`15` §12, 2026-08-14).
 */
export type PropagationChoice = 'unset' | 'no' | 'yes';

/** The global-rate form, all strings — a form field holds text until it is validated. */
export interface TmrRateForm {
  rateTypeId: string;
  creditEngineTypeId: string;
  minInstallment: string;
  maxInstallment: string;
  value: string;
  /** Only meaningful when editing: creating a line never reaches an existing Empresa. */
  propagate: PropagationChoice;
}

/** A blank form, defaulted to the band the template's own lines mostly use. */
export function emptyTmrRateForm(): TmrRateForm {
  return {
    rateTypeId: '',
    creditEngineTypeId: '',
    minInstallment: '1',
    maxInstallment: '12',
    value: '',
    propagate: 'unset',
  };
}

/**
 * The form as it starts when editing an existing line.
 *
 * `propagate` starts unset even here — especially here: the value is the field being changed, and the
 * question is about that change, not about the line.
 *
 * @param rate The template line being edited.
 */
export function toTmrRateForm(rate: TmrRate): TmrRateForm {
  return {
    rateTypeId: String(rate.rateTypeId),
    creditEngineTypeId: rate.creditEngineTypeId == null ? '' : String(rate.creditEngineTypeId),
    minInstallment: String(rate.minInstallment),
    maxInstallment: String(rate.maxInstallment),
    value: rate.value.toFixed(2).replace('.', ','),
    propagate: 'unset',
  };
}

/** Field-level messages, keyed by form field. Empty means the form may be submitted. */
export type TmrRateFormErrors = Partial<Record<keyof TmrRateForm, string>>;

/**
 * Validates the global-rate form.
 *
 * Like the Empresa's rate form it does **not** check band overlap: the template is stricter about it than
 * an Empresa's rates are, and the rule that decides lives on the server (`rates.global_overlap`). A second
 * implementation here would eventually disagree with the one that refuses the write.
 *
 * There is no ceiling on the value either — any value at or above zero is accepted, by decision of
 * 2026-08-10 (`15` §12).
 *
 * @param form The form as typed.
 * @param editing Whether this is a change to an existing line, which is what makes propagation a question.
 */
export function validateTmrRateForm(form: TmrRateForm, editing: boolean): TmrRateFormErrors {
  const errors: TmrRateFormErrors = {};

  if (!form.rateTypeId) errors.rateTypeId = 'Escolha o tipo da taxa';

  const min = parseInteger(form.minInstallment);
  const max = parseInteger(form.maxInstallment);

  if (min == null || min < 0) errors.minInstallment = 'Informe a parcela inicial';
  if (max == null || max < 0) errors.maxInstallment = 'Informe a parcela final';
  if (min != null && max != null && max < min) {
    errors.maxInstallment = 'A parcela final não pode ser menor que a inicial';
  }

  const value = parseDecimal(form.value);
  if (value == null) errors.value = 'Informe o valor da taxa';
  else if (value < 0) errors.value = 'O valor não pode ser negativo';

  if (editing && form.propagate === 'unset') {
    errors.propagate = 'Escolha se a mudança alcança as empresas que já existem';
  }

  return errors;
}

/**
 * The create payload. Call only on a form {@link validateTmrRateForm} accepted.
 *
 * @param form The validated form.
 */
export function toCreateTmrRateRequest(form: TmrRateForm): CreateTmrRateRequest {
  return {
    rateTypeId: parseInteger(form.rateTypeId)!,
    creditEngineTypeId: form.creditEngineTypeId ? parseInteger(form.creditEngineTypeId) : null,
    minInstallment: parseInteger(form.minInstallment)!,
    maxInstallment: parseInteger(form.maxInstallment)!,
    value: parseDecimal(form.value)!,
  };
}

/**
 * The update payload, carrying the propagation the operator chose.
 *
 * `unset` maps to `false` as a last line of defence, so a bug that let it through would fail closed — not
 * rewriting a thousand live rates. The validator is what actually stops it.
 *
 * @param form The validated form.
 */
export function toUpdateTmrRateRequest(form: TmrRateForm): UpdateTmrRateRequest {
  return { ...toCreateTmrRateRequest(form), propagate: form.propagate === 'yes' };
}

/**
 * An integer as typed. Null for anything else, including a decimal — an installment count is whole.
 *
 * @param raw The typed text.
 */
function parseInteger(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}
